import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSkill } from '../../hooks/useSkills';
import { SkillPreview } from '../../components/skills/SkillPreview';
import { SkillAttachmentList } from '../../components/skills/SkillAttachmentList';
import { ExportDialog } from '../../components/skills/ExportDialog';
import { Spinner } from '../../components/common/Spinner';
import { FiArrowLeft } from 'react-icons/fi';

type Tab = 'preview' | 'raw' | 'sections' | 'usage';

export const SkillDetail: React.FC = () => {
  const { skillName } = useParams<{ skillName: string }>();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [showExport, setShowExport] = useState(false);

  const { data: skill, isLoading, error } = useSkill(skillName ?? '');

  if (isLoading) {
    return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;
  }

  if (error || !skill) {
    return (
      <div className="text-center py-12 text-red-500">
        Skill not found
        <button onClick={() => navigate('/skills')} className="block mx-auto mt-4 text-blue-500 text-sm">
          ← Back to Skills
        </button>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'preview', label: 'Preview' },
    { id: 'raw', label: 'Raw' },
    { id: 'sections', label: `Sections (${skill.sections.length})` },
    { id: 'usage', label: 'Usage' },
  ];

  return (
    <div className={`container mx-auto p-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      {/* Back */}
      <button
        onClick={() => navigate('/skills')}
        className={`flex items-center gap-1 text-sm mb-4 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'}`}
      >
        <FiArrowLeft /> Back to Skills
      </button>

      {/* Header */}
      <div className={`rounded-lg p-6 mb-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{skill.display_name}</h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{skill.description}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                v{skill.version}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {skill.trust_tier}
              </span>
              {skill.estimated_tokens > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                  ~{skill.estimated_tokens.toLocaleString()} tokens
                </span>
              )}
              {skill.source_format && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-700'}`}>
                  {skill.source_format}
                </span>
              )}
              {skill.tags.map((tag) => (
                <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  {tag}
                </span>
              ))}
            </div>
            {skill.compatibility && skill.compatibility.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Compatible with:</span>
                {skill.compatibility.map((c) => (
                  <span key={c} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'}`}>
                    {c.replace('_', ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowExport(true)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium text-white flex-shrink-0 ${
              darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex border-b mb-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? darkMode ? 'border-b-2 border-blue-400 text-blue-300' : 'border-b-2 border-blue-500 text-blue-600'
                : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        {activeTab === 'preview' && <SkillPreview content={skill.body} mode="preview" />}
        {activeTab === 'raw' && <SkillPreview content={skill.raw_body ?? skill.body} mode="raw" />}
        {activeTab === 'sections' && (
          <div className="p-4">
            {skill.sections.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No sections detected</p>
            ) : (
              <ul className="space-y-2">
                {skill.sections.map((s, i) => (
                  <li key={i} className={`flex items-center gap-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                      H{s.level}
                    </span>
                    <span>{s.heading}</span>
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      lines {s.line_start}–{s.line_end}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {activeTab === 'usage' && (
          <div className="p-4 space-y-4">
            <div>
              <p className={`text-xs mb-2 font-medium uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Source
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {skill.source_id || 'Unknown'} · {skill.source_path || 'N/A'}
              </p>
            </div>
            <div>
              <p className={`text-xs mb-2 font-medium uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Attached To
              </p>
              <SkillAttachmentList skillName={skill.name} entityType="persona" entityId="" />
            </div>
          </div>
        )}
      </div>

      <ExportDialog
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        initialSkillNames={[skill.name]}
        availableSkills={[skill]}
      />
    </div>
  );
};
